import React, { useState, useEffect } from 'react';
import apiClient from '../../../api/client';

interface PropertiesTabProps {
  molecule: {
    smiles: string;
    properties: Record<string, any>;
    mw?: number | null;
    logp?: number | null;
    tpsa?: number | null;
    hbd?: number | null;
    hba?: number | null;
    rotatable_bonds?: number | null;
    ring_count?: number | null;
    exact_mass?: number | null;
    formula?: string | null;
    aromatic_rings?: number | null;
    heavy_atoms?: number | null;
    formal_charge?: number | null;
    fsp3?: number | null;
    molar_refractivity?: number | null;
    stereocenter_count?: number | null;
    inchi_key?: string | null;
    inchi?: string | null;
  };
}

// Colour-coding helpers
const getMWColor = (val: number): string | undefined => {
  if (val > 500) return '#d97706'; // amber
  return undefined;
};

const getLogPColor = (val: number): string | undefined => {
  if (val > 5) return '#dc2626'; // red
  if (val > 3.5) return '#d97706'; // amber
  return undefined;
};

const getIC50Color = (val: number): string | undefined => {
  if (val < 10) return '#16a34a'; // green
  if (val > 50) return '#d97706'; // amber
  return undefined;
};

const getClearanceColor = (val: string): string | undefined => {
  const lower = val.toLowerCase();
  if (lower === 'low' || lower.includes('low')) return '#16a34a';
  if (lower === 'medium' || lower.includes('medium') || lower.includes('moderate')) return '#d97706';
  if (lower === 'high' || lower.includes('high')) return '#dc2626';
  return undefined;
};

interface PropRow {
  label: string;
  value: string | number;
  color?: string;
  copyable?: boolean;
}

const PropertyRow: React.FC<{ row: PropRow }> = ({ row }) => (
  <div
    className="flex justify-between items-start py-1.5 px-2"
    style={{ borderBottom: '1px solid var(--card-border)' }}
  >
    <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{row.label}</span>
    <span
      className={`text-xs font-mono text-right max-w-[220px] truncate ${row.copyable ? 'cursor-pointer' : ''}`}
      style={{ color: row.color || 'var(--text-primary)' }}
      title={row.copyable ? `Click to copy: ${row.value}` : String(row.value)}
      onClick={row.copyable ? () => navigator.clipboard.writeText(String(row.value)) : undefined}
    >
      {String(row.value)}
    </span>
  </div>
);

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <h4
    className="text-[10px] font-semibold uppercase tracking-wider mt-3 mb-1 px-2"
    style={{ color: 'var(--text-muted)' }}
  >
    {title}
  </h4>
);

const PropertiesTab: React.FC<PropertiesTabProps> = ({ molecule }) => {
  const [calculated, setCalculated] = useState<Record<string, any> | null>(null);

  // Fetch calculated properties if none are available
  useEffect(() => {
    const hasMW = molecule.mw != null || molecule.properties?.MW != null;
    if (!hasMW && molecule.smiles) {
      apiClient.post('/v2/properties/calculate', { smiles: molecule.smiles })
        .then(r => setCalculated(r.data))
        .catch(() => {});
    }
  }, [molecule.smiles]); // eslint-disable-line react-hooks/exhaustive-deps

  const props = { ...(molecule.properties || {}), ...(calculated || {}) };

  // --- Calculated Properties ---
  const calcProps: PropRow[] = [];
  const addCalc = (label: string, value: any, colorFn?: (v: number) => string | undefined) => {
    if (value != null && value !== '') {
      const numVal = typeof value === 'number' ? Math.round(value * 100) / 100 : value;
      const color = colorFn && typeof value === 'number' ? colorFn(value) : undefined;
      calcProps.push({ label, value: numVal, color });
    }
  };

  addCalc('Molecular Weight', molecule.mw ?? props.MW ?? props.mw, getMWColor);
  addCalc('LogP', molecule.logp ?? props.LogP ?? props.logp, getLogPColor);
  addCalc('TPSA', molecule.tpsa ?? props.TPSA ?? props.tpsa);
  addCalc('H-Bond Acceptors', molecule.hba ?? props.HBA ?? props.hba);
  addCalc('H-Bond Donors', molecule.hbd ?? props.HBD ?? props.hbd);
  addCalc('Rotatable Bonds', molecule.rotatable_bonds ?? props.RotBonds ?? props.rotatable_bonds);
  addCalc('Ring Count', molecule.ring_count ?? props.Rings ?? props.ring_count);
  addCalc('Exact Mass', molecule.exact_mass ?? props.exact_mass);
  addCalc('Aromatic Rings', molecule.aromatic_rings ?? props.aromatic_rings);
  addCalc('Heavy Atoms', molecule.heavy_atoms ?? props.heavy_atoms);
  addCalc('Formal Charge', molecule.formal_charge ?? props.formal_charge);
  addCalc('Fraction Csp3', molecule.fsp3 ?? props.fsp3);
  addCalc('Molar Refractivity', molecule.molar_refractivity ?? props.molar_refractivity);
  addCalc('Stereocentres', molecule.stereocenter_count ?? props.stereocenter_count);

  // --- Activity data (IC50 and other assay data) ---
  const activityProps: PropRow[] = [];
  const activityKeys = ['IC50', 'ic50', 'EC50', 'ec50', 'Ki', 'ki', 'Kd', 'kd', 'pIC50', 'pEC50', 'Activity', 'Potency'];
  const activitySkipSet = new Set<string>();
  for (const key of activityKeys) {
    const matchKey = Object.keys(props).find(k => k.toLowerCase() === key.toLowerCase());
    if (matchKey && props[matchKey] != null && String(props[matchKey]).trim()) {
      const val = props[matchKey];
      const numVal = typeof val === 'number' ? val : parseFloat(val);
      const colorFn = matchKey.toLowerCase().includes('ic50') ? getIC50Color : undefined;
      activityProps.push({
        label: matchKey,
        value: typeof val === 'number' ? Math.round(val * 100) / 100 : val,
        color: colorFn && !isNaN(numVal) ? colorFn(numVal) : undefined,
      });
      activitySkipSet.add(matchKey);
    }
  }

  // --- ADMET data ---
  const admetProps: PropRow[] = [];
  const admetKeys = ['Clearance', 'clearance', 'HLM', 'RLM', 'Solubility', 'solubility', 'Permeability', 'permeability', 'hERG', 'CYP', 'Caco2', 'PAMPA', 'Metabolic Stability'];
  const admetSkipSet = new Set<string>();
  for (const key of admetKeys) {
    const matchKey = Object.keys(props).find(k => k.toLowerCase() === key.toLowerCase());
    if (matchKey && props[matchKey] != null && String(props[matchKey]).trim()) {
      const val = props[matchKey];
      const color = matchKey.toLowerCase().includes('clearance') && typeof val === 'string' ? getClearanceColor(val) : undefined;
      admetProps.push({
        label: matchKey,
        value: typeof val === 'number' ? Math.round(val * 100) / 100 : val,
        color,
      });
      admetSkipSet.add(matchKey);
    }
  }

  // --- Identifiers ---
  const identifiers: PropRow[] = [];
  if (molecule.smiles) identifiers.push({ label: 'SMILES', value: molecule.smiles, copyable: true });

  const formula = (molecule as any).formula ?? props.formula;
  if (formula) identifiers.push({ label: 'Molecular Formula', value: formula, copyable: true });

  const inchi = (molecule as any).inchi ?? props.InChI ?? props.inchi;
  if (inchi) identifiers.push({ label: 'InChI', value: inchi, copyable: true });

  const inchiKey = (molecule as any).inchi_key ?? props.InChIKey ?? props.inchi_key ?? props.InChI_Key;
  if (inchiKey) identifiers.push({ label: 'InChIKey', value: inchiKey, copyable: true });

  // --- Custom / File Properties (everything else) ---
  const skipKeys = new Set([
    'MW', 'mw', 'LogP', 'logp', 'TPSA', 'tpsa', 'HBD', 'hbd', 'HBA', 'hba',
    'RotBonds', 'rotatable_bonds', 'Rings', 'ring_count', 'exact_mass',
    'aromatic_rings', 'heavy_atoms', 'formal_charge', 'fsp3',
    'molar_refractivity', 'stereocenter_count', 'formula',
    'Structure', 'structure', 'molfile', 'svg', 'image',
    'InChI', 'inchi', 'InChIKey', 'inchi_key', 'InChI_Key',
    'input_smiles', 'canonical_smiles', 'isomeric_smiles', 'nonisomeric_smiles',
    'patent_count_exact', 'patent_count_similar', 'patent_max_zone', 'ip_status',
    'target_count', 'pathway_count', 'reaction_count',
    ...Array.from(activitySkipSet),
    ...Array.from(admetSkipSet),
  ]);

  const customProps = Object.entries(props)
    .filter(([key]) => !skipKeys.has(key))
    .filter(([, value]) => value != null && String(value).trim() !== '');

  return (
    <div className="space-y-1">
      {/* Calculated Properties */}
      {calcProps.length > 0 && (
        <div>
          <SectionHeader title="Calculated Properties" />
          {calcProps.map((row) => (
            <PropertyRow key={row.label} row={row} />
          ))}
        </div>
      )}

      {/* Activity */}
      {activityProps.length > 0 && (
        <div>
          <SectionHeader title="Activity" />
          {activityProps.map((row) => (
            <PropertyRow key={row.label} row={row} />
          ))}
        </div>
      )}

      {/* ADMET */}
      {admetProps.length > 0 && (
        <div>
          <SectionHeader title="ADMET" />
          {admetProps.map((row) => (
            <PropertyRow key={row.label} row={row} />
          ))}
        </div>
      )}

      {/* Identifiers */}
      {identifiers.length > 0 && (
        <div>
          <SectionHeader title="Identifiers" />
          {identifiers.map((row) => (
            <PropertyRow key={row.label} row={row} />
          ))}
        </div>
      )}

      {/* File Properties */}
      {customProps.length > 0 && (
        <div>
          <SectionHeader title={`File Properties (${customProps.length})`} />
          {customProps.map(([key, value]) => (
            <PropertyRow key={key} row={{
              label: key,
              value: typeof value === 'number' ? Math.round(value * 100) / 100 : String(value),
            }} />
          ))}
        </div>
      )}
    </div>
  );
};

export default PropertiesTab;
